import { Box, Container, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import CallToAction from '../CallToAction';
import { displayTitleSx } from '../../styles/typography';

// Seconds trimmed off the head of the hero clip. The source file lives on the CDN,
// so the trim happens at playback: we seek past the intro and loop back to here.
const HERO_VIDEO_START = 3.5;

// A plain banner: fixed height, never full-screen, and the page scrolls straight past
// it. Shorter on small screens so the services below are reachable in one swipe.
const HERO_HEIGHT = { xs: '58vh', sm: '75vh', md: '85vh' };

// On phones the 16:9 clip covers a near-portrait banner, so it fills the height
// exactly and the captions burnt into the bottom of the frame stay in shot. Running
// the video layer taller than the banner and pinning it to the top lets the banner's
// own overflow clip that strip off the bottom. Wider screens need no trim.
const HERO_VIDEO_HEIGHT = { xs: '112%', sm: '100%' };

// Services data. Card images are real photographs only, the previous *-service.png
// banners were AI-generated and carried garbled/incorrect Hallidays branding.
const services = [
  {
    title: "Builders",
    image: "/img/img-builders.jpg",
    description: "Partner with us for all your heating and cooling needs in new constructions.",
    link: "/builders",
    buttonText: "Learn More"
  },
  {
    title: "Products & Services",
    image: "/img/sitting-room.jpg",
    description: "High-quality heating and cooling systems, design to installation.",
    link: "/products-and-services",
    buttonText: "Learn More"
  },
  {
    title: "Support",
    image: "/img/support-technician.jpg",
    description: "Get instant help with our AI assistant or browse through our FAQ section.",
    link: "/support",
    buttonText: "Get Help"
  }
];

const Home = () => {
  // The tagline fades in a beat after load rather than being painted with the frame.
  const [heroVisible, setHeroVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Only true once the comfort photo has actually failed to load. The fallback copy
  // is rendered conditionally rather than stacked behind the photo, so it never sits
  // in the DOM to be picked up by screen readers or text selection.
  const [comfortImageFailed, setComfortImageFailed] = useState(false);

  // Skip the first HERO_VIDEO_START seconds, both on first play and on every loop.
  const seekPastIntro = () => {
    const video = videoRef.current;
    if (video && video.currentTime < HERO_VIDEO_START) {
      video.currentTime = HERO_VIDEO_START;
    }
  };

  useEffect(() => {
    // Flip on the next frame rather than at first render, so the browser has a hidden
    // state to transition *from*, setting it during render would skip the animation.
    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setHeroVisible(true));
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, []);

  return (
    <Box>
      {/* Hero Section - a plain banner that scrolls with the rest of the page */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: HERO_HEIGHT,
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: HERO_VIDEO_HEIGHT
        }}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onLoadedMetadata={seekPastIntro}
            // `loop` would restart at 0, so we loop manually back to the trim point
            onEnded={() => {
              const video = videoRef.current;
              if (!video) return;
              video.currentTime = HERO_VIDEO_START;
              video.play().catch(() => {});
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center'
            }}
          >
            <source src="https://452f47a14963441e316e-f9c98182ad3d4cc3668825cfc37dae36.ssl.cf4.rackcdn.com/Hallidays-16x9-cropped-2.mp4" type="video/mp4" />
          </video>
        </Box>
        
        {/* Overlay with content */}
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          zIndex: 1
        }}>
          <Container maxWidth="xl">
            <Box maxWidth={{ xs: '100%', md: '900px' }} sx={{
              px: { xs: 2, md: 0 },
              py: { xs: 3, sm: 0 }, // Added vertical padding on mobile
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease, transform 1s ease',
            }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '1.9rem', sm: '2.75rem', md: '3.4rem', lg: '4rem' },
                  fontWeight: 700,
                  color: '#ffffff',
                  lineHeight: 1.1,
                  mb: { xs: 2, sm: 3 },
                  width: '100%',
                  display: 'block',
                  // nowrap only where the line actually fits. Below lg it would push the
                  // headline past the viewport edge and give the page a sideways scroll.
                  whiteSpace: { xs: 'normal', lg: 'nowrap' },
                  '& br': { display: 'block' } // Show br on all screens
                }}
              >
                You build their dream home,<br />
                We design their comfort
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.3rem', md: '1.5rem' }, // Smaller on mobile
                  color: '#ffffff',
                  opacity: 0.9
                }}
              >
                The No. 1 Choice for Victoria's Leading Builders
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Services Section */}
      <Box sx={{ 
        py: { xs: 5, md: 10 },
        px: { xs: 2, md: 4 },
        bgcolor: '#fff'
      }}>
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            align="center"
            sx={{
              ...displayTitleSx,
              mb: { xs: 4, md: 5 },
            }}
          >
            Our Services
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 3, md: 4 },
            width: '100%',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'stretch' },
          }}>
            {services.map((service, index) => (
              <Box 
                key={index}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 0' }, // Equal width columns on desktop
                  // No explicit height, lets alignItems: 'stretch' match all cards to the tallest
                  minHeight: { xs: 'auto', md: '380px' },
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.1)'
                  },
                  mb: { xs: 3, md: 0 }, // Add bottom margin on mobile only
                }}
              >
                <Box sx={{ 
                  width: '100%', 
                  height: { xs: '180px', md: '200px' },
                  overflow: 'hidden' 
                }}>
                  <img 
                    src={service.image}
                    alt={`${service.title} Service`}
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
                <Box sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  display: 'flex', 
                  flexDirection: 'column',
                  flexGrow: 1,
                  justifyContent: 'space-between'
                }}>
                  <Typography 
                    variant="h5" 
                    align="center"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      mb: 2
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography 
                    align="center"
                    sx={{ 
                      mb: 3,
                      flexGrow: 1,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    {service.description}
                  </Typography>
                  <Button 
                    component={RouterLink}
                    to={service.link}
                    variant="contained"
                    color="primary"
                    sx={{ 
                      alignSelf: 'center',
                      minWidth: '120px'
                    }}
                  >
                    {service.buttonText}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Experience Section. Also the target of the "Our Story" nav link. */}
      <Box
        id="our-story"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          bgcolor: '#f8fbff'
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 5, md: 8 }
            }}
          >
            {/* Left Content */}
            <Box
              sx={{
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Typography
                sx={{
                  color: '#0066b2',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  mb: 1.5
                }}
              >
                Our Story
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  // Same treatment as the "Our Services" heading above: theme h2
                  // (Poppins 600, #1a2b3c) at the shared display size.
                  ...displayTitleSx,
                  mb: 3
                }}
              >
                Quality professional service{' '}
                <Box component="span" sx={{ color: '#0066b2' }}>since 1969</Box>
              </Typography>
              <Typography
                sx={{
                  color: '#445668',
                  lineHeight: 1.8,
                  mb: 2,
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }}
              >
                The Hallidays story began in 1969, established in Melbourne as a family
                owned and operated business with an ethos centred on customer service and
                quality. We have grown to become Victoria's leading supplier and installer
                of quality heating and cooling to Victoria's top builders.
              </Typography>
              <Typography
                sx={{
                  color: '#445668',
                  lineHeight: 1.8,
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }}
              >
                With over 50 years of experience, our Specialist Consultants provide a
                concept to completion approach for builders. We understand the intricacies
                of design, and provide pricing and solutions to meet the architecture,
                construction and comfort needs of each individual home.
              </Typography>
            </Box>

            {/* Right Image, with the founding stamp overlapping its corner */}
            <Box
              sx={{
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Box sx={{ position: 'relative', p: { xs: 1, md: 2 } }}>
                <img
                  src="/img/experience.jpg"
                  alt="Halliday technicians at work"
                  style={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 102, 178, 0.08)',
                    position: 'relative',
                    zIndex: 1,
                    display: 'block'
                  }}
                />
                <Box
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 20,
                    bottom: 20,
                    border: '2px solid rgba(0, 102, 178, 0.1)',
                    borderRadius: '16px',
                    zIndex: 0
                  }}
                />
                {/* Founding stamp */}
                <Box
                  sx={{
                    position: 'absolute',
                    zIndex: 2,
                    bottom: { xs: -12, md: -20 },
                    left: { xs: -8, md: -32 },
                    width: { xs: 96, md: 128 },
                    height: { xs: 96, md: 128 },
                    borderRadius: '50%',
                    bgcolor: '#ffffff',
                    border: '2px solid #0066b2',
                    boxShadow: '0 8px 20px rgba(0, 102, 178, 0.18)',
                    transform: 'rotate(-8deg)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    sx={{
                      color: '#0066b2',
                      fontSize: { xs: '0.6rem', md: '0.7rem' },
                      fontWeight: 700,
                      letterSpacing: '0.18em'
                    }}
                  >
                    EST.
                  </Typography>
                  <Typography
                    sx={{
                      color: '#0066b2',
                      fontSize: { xs: '1.6rem', md: '2.1rem' },
                      fontWeight: 700,
                      lineHeight: 1
                    }}
                  >
                    1969
                  </Typography>
                  <Typography
                    sx={{
                      color: '#445668',
                      fontSize: { xs: '0.5rem', md: '0.6rem' },
                      fontWeight: 600,
                      letterSpacing: '0.14em',
                      mt: 0.25
                    }}
                  >
                    MELBOURNE
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

        </Container>
      </Box>

      {/* Comfort Section. Image left, copy right, on a baby blue aurora ground: a few
          soft off-centre radial washes over a pale blue base, so the light pools rather
          than banding the way a linear gradient does. Ties back to the brand blue and
          reads as "cooling" alongside the heating/cooling copy. */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          bgcolor: '#f7fbfe',
          backgroundImage: `
            radial-gradient(60% 55% at 12% 18%, rgba(137, 207, 240, 0.22) 0%, rgba(137, 207, 240, 0) 60%),
            radial-gradient(55% 60% at 88% 12%, rgba(0, 102, 178, 0.07) 0%, rgba(0, 102, 178, 0) 62%),
            radial-gradient(70% 60% at 72% 88%, rgba(168, 224, 255, 0.24) 0%, rgba(168, 224, 255, 0) 65%),
            radial-gradient(50% 50% at 30% 95%, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 60%)
          `
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              // row-reverse rather than reordering the markup: the copy stays first in
              // the DOM, so it is still what gets read and what stacks on top on mobile.
              flexDirection: { xs: 'column', sm: 'row-reverse' },
              alignItems: 'center',
              gap: { xs: 5, md: 8 }
            }}
          >
            {/* Copy, sitting on the right */}
            <Box
              sx={{
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Typography
                sx={{
                  color: '#0066b2',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  mb: 1.5
                }}
              >
                Year-round comfort
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  // Same treatment as the other section headings on this page
                  // ("Our Services", "Quality professional service since 1969").
                  ...displayTitleSx,
                  mb: 3
                }}
              >
                Be comfortable all year round
              </Typography>
              <Typography
                sx={{
                  color: '#445668',
                  lineHeight: 1.8,
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }}
              >
                Heating for the cold months, cooling for the hot ones, sized and installed
                before the plaster goes on.
              </Typography>
            </Box>

            {/* Image, sitting on the left */}
            <Box
              sx={{
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Box sx={{ position: 'relative', p: { xs: 1, md: 2 } }}>
                {/* If the photo ever fails to load the slot shows an obvious labelled
                    frame rather than collapsing to nothing, which is how the previous
                    version of this section went unnoticed. */}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    // 3:2 to match the artwork exactly. A 4:3 slot would crop the
                    // sides and cut into the air conditioner at top left.
                    aspectRatio: '3 / 2',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    bgcolor: '#eaf3fb',
                    boxShadow: '0 8px 24px rgba(0, 102, 178, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {comfortImageFailed && (
                    <Typography
                      sx={{
                        color: '#5b7a92',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                        px: 2
                      }}
                    >
                      Add /img/comfort-aircon.jpg
                    </Typography>
                  )}
                  <Box
                    component="img"
                    src="/img/comfort-aircon.jpg"
                    alt="A young woman relaxing on a bean bag in a sunlit living room, beneath a wall-mounted split system air conditioner"
                    onError={() => setComfortImageFailed(true)}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      // Hidden rather than unmounted, so a transient failure can still
                      // recover if the browser retries the request.
                      display: comfortImageFailed ? 'none' : 'block'
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 20,
                    bottom: 20,
                    border: '2px solid rgba(0, 102, 178, 0.1)',
                    borderRadius: '16px',
                    zIndex: 0
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <CallToAction />

    </Box>
  );
};

export default Home; 